import React, { useState, useEffect, useCallback } from 'react';
import { TechnischeRessource } from '../../../types/network';
import { PvGenerationData, getWeatherIcon, getEfficiencyColor, formatPower, formatEfficiency } from '../../../types/pv';
import { pvAnlageApi } from '../../../lib/api/pvAnlage';
import { parseUtcTimestamp } from '../../../utils/timeUtils';
import { useHistoricalPlaybackStore } from '../../../store/historicalPlaybackStore';
import { fmt } from '../../../utils/numberFormat';

interface PvGenerationDisplayProps {
  pvAnlage: TechnischeRessource;
  hausanschlussId: string;
  onUpdate: (pvAnlage: TechnischeRessource) => void;
}

export const PvGenerationDisplay: React.FC<PvGenerationDisplayProps> = ({
  pvAnlage,
  hausanschlussId,
  onUpdate: _onUpdate
}) => {
  const [generationData, setGenerationData] = useState<PvGenerationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Historical playback: the widget is live-only (current generation, live
  // weather) — hide it instead of showing "now" values at a rewound instant.
  const isHistoricalPlayback = useHistoricalPlaybackStore(state => state.mode !== 'live');

  const loadGenerationData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pvAnlageApi.getCurrentGeneration(hausanschlussId, pvAnlage.id);
      setGenerationData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erzeugungsdaten konnten nicht geladen werden');
      console.error('Error loading PV generation data:', err);
    } finally {
      setLoading(false);
    }
  }, [hausanschlussId, pvAnlage.id]);

  // Load generation data (live only — pointless at a rewound instant)
  useEffect(() => {
    if (pvAnlage.type === 'pv_anlage' && !isHistoricalPlayback) {
      loadGenerationData();
    }
  }, [pvAnlage.type, loadGenerationData, isHistoricalPlayback]);

  const handleRefresh = async () => {
    await loadGenerationData();
  };

  if (pvAnlage.type !== 'pv_anlage') {
    return null;
  }

  if (isHistoricalPlayback) {
    return (
      <div className="bg-gray-50 dark:bg-primary-700 p-3 rounded-lg border border-gray-200 dark:border-primary-600">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg">☀️</span>
          <span className="text-sm font-medium text-gray-900 dark:text-primary-100">PV-Erzeugung</span>
        </div>
        <div className="text-xs text-gray-400 dark:text-primary-400 italic">
          Live-Wert ausgeblendet (historische Ansicht)
          {(pvAnlage.maxPower ?? 0) > 0 && <> · max {formatPower(pvAnlage.maxPower ?? 0)}</>}
        </div>
      </div>
    );
  }

  const currentGeneration = generationData?.currentGenerationKw || pvAnlage.currentPowerGenerationKw || 0;
  const maxGeneration = generationData?.maxPossibleGenerationKw || pvAnlage.maxPower || 0;
  const efficiency = maxGeneration > 0 ? (currentGeneration / maxGeneration) * 100 : 0;
  const efficiencyColor = getEfficiencyColor(efficiency);

  return (
    <div className="pv-generation-display space-y-3">
      {/* Current Generation Status */}
      <div className="bg-gray-50 dark:bg-primary-700 p-3 rounded-lg border border-gray-200 dark:border-primary-600">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-lg">
              {generationData?.weatherData ? getWeatherIcon(generationData.weatherData.description) : '☀️'}
            </span>
            <span className="text-sm font-medium text-gray-900 dark:text-primary-100">Aktuelle Erzeugung</span>
            {/* The value is the measurement pool's weather-based estimate
                (PvCalculationService: irradiance × installed power, or the panel
                model) — never a meter reading of this house. Same badge style as
                the SMGW section. */}
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 whitespace-nowrap"
              title="Geschätzter Wert aus Wetterdaten (Einstrahlung, Temperatur, Bewölkung) und installierter Leistung — kein Messwert dieses Hausanschlusses. Die gemessene Einspeisung steht, falls vorhanden, im Abschnitt Smart Meter Gateway."
            >
              geschätzt
            </span>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="text-xs text-info-600 dark:text-info-400 hover:text-info-700 dark:hover:text-info-300 disabled:opacity-50 transition-colors"
            title="Erzeugungsdaten aktualisieren"
          >
            {loading ? '⟳' : '↻'}
          </button>
        </div>

        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-xl font-bold text-success-600 dark:text-success-400">
            {formatPower(currentGeneration)}
          </span>
          <span className="text-sm text-gray-600 dark:text-primary-300">
            / {formatPower(maxGeneration)} max
          </span>
        </div>

        {/* Efficiency Bar */}
        <div className="w-full bg-gray-200 dark:bg-primary-600 rounded-full h-2 mb-2">
          <div
            className="h-2 rounded-full transition-all duration-300"
            style={{
              width: `${Math.min(100, efficiency)}%`,
              backgroundColor: efficiencyColor
            }}
          />
        </div>

        <div className="flex justify-between text-xs text-gray-600 dark:text-primary-300">
          <span>Effizienz: {formatEfficiency(efficiency)}</span>
          {generationData?.lastUpdated && (
            <span>
              Aktualisiert: {parseUtcTimestamp(generationData.lastUpdated).toLocaleTimeString('de-DE')}
            </span>
          )}
        </div>

        {error && (
          <div className="text-xs text-error-600 dark:text-error-400 mt-1">
            {error}
          </div>
        )}
      </div>

      {/* Weather Information */}
      {generationData?.weatherData && (
        <div className="bg-info-50 dark:bg-info-900/20 p-2 rounded text-xs border border-info-200 dark:border-info-800">
          <div className="flex items-center justify-between text-gray-900 dark:text-primary-100">
            <span className="font-medium">Wetterbedingungen</span>
            <span>{generationData.weatherData.description}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-1 text-gray-600 dark:text-primary-300">
            <div>☀️ {fmt(generationData.weatherData.solarIrradiance, 0)} W/m²</div>
            <div>🌡️ {fmt(generationData.weatherData.temperature, 1)}°C</div>
            <div>☁️ {fmt(generationData.weatherData.cloudCover, 0)}%</div>
            <div>💨 {fmt(generationData.weatherData.windSpeed, 1)} m/s</div>
          </div>
        </div>
      )}

    </div>
  );
};